import { CapacitorHttp } from '@capacitor/core';
import { isNativeRuntime } from '@/lib/runtime';

const MOBILE_TOKEN_KEY = 'zapt_mobile_access_token';
const MOBILE_FETCH_PATCH_KEY = '__zaptMobileFetchPatched';

function getSafeStorage() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function getMobileAccessToken() {
  const storage = getSafeStorage();
  return storage?.getItem(MOBILE_TOKEN_KEY) || '';
}

export function setMobileAccessToken(token) {
  const storage = getSafeStorage();
  if (!storage) return;

  if (token) {
    storage.setItem(MOBILE_TOKEN_KEY, token);
  } else {
    storage.removeItem(MOBILE_TOKEN_KEY);
  }
}

export function clearMobileAccessToken() {
  setMobileAccessToken('');
}

export function installMobileAuthFetchShim() {
  if (typeof window === 'undefined' || !isNativeRuntime()) return;
  if (window[MOBILE_FETCH_PATCH_KEY]) return;

  const originalFetch = window.fetch.bind(window);

  const toNativeResponse = (payload, status = 200, responseHeaders = {}) => {
    const normalizedHeaders = new Headers(responseHeaders || {});
    const body =
      payload == null
        ? ''
        : typeof payload === 'string'
          ? payload
          : JSON.stringify(payload);

    return new Response(body, {
      status,
      headers: normalizedHeaders,
    });
  };

  const parseNativeRequestBody = (body, headers) => {
    if (!body) return undefined;
    if (typeof body !== 'string') return body;

    const contentType = headers.get('Content-Type') || headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      try {
        return JSON.parse(body);
      } catch {
        return body;
      }
    }

    return body;
  };

  window.fetch = async (input, init = {}) => {
    const token = getMobileAccessToken();
    const inputHeaders = input instanceof Request ? input.headers : undefined;
    const headers = new Headers(init.headers || inputHeaders);

    if (!headers.has('X-Zapt-Client')) {
      headers.set('X-Zapt-Client', 'mobile');
    }

    if (token && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const nextInit = {
      ...init,
      headers,
      credentials: token ? 'omit' : (init.credentials || 'same-origin'),
    };

    const body = nextInit.body ?? (input instanceof Request ? input.body : undefined);
    const usesComplexBody =
      typeof FormData !== 'undefined' && body instanceof FormData
      || typeof Blob !== 'undefined' && body instanceof Blob
      || typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer;

    if (usesComplexBody) {
      return originalFetch(input, nextInit);
    }

    try {
      const method = (nextInit.method || (input instanceof Request ? input.method : 'GET') || 'GET').toUpperCase();
      const url = typeof input === 'string' ? input : input instanceof Request ? input.url : String(input);
      const data = parseNativeRequestBody(body, headers);
      const nativeResponse = await CapacitorHttp.request({
        url,
        method,
        headers: Object.fromEntries(headers.entries()),
        data,
      });

      return toNativeResponse(nativeResponse?.data, nativeResponse?.status || 200, nativeResponse?.headers || {});
    } catch (error) {
      console.warn('Falling back to web fetch after native HTTP failure', error);
      return originalFetch(input, nextInit);
    }
  };

  window[MOBILE_FETCH_PATCH_KEY] = true;
}
